import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Edit, ListChecks } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import {
    getAdminQuestionGroupsUrl,
    getAdminQuestionGroupByIdUrl,
    getAdminQuestionGroupPostUrl,
    getAdminQuestionGroupPutUrl,
    getAdminQuestionGroupDeleteUrl,
    getTagUrl,
} from '../utils/UrlUtilities';
import { constructQuestionGroupPayload } from '../utils/PayloadUtils';
import { TAGLEVELS, DIVISION_VALUES, UNIT_VALUES, isDivisionLevel, isUnitLevel, isLanguageLevel, defaultPageSize, itemsPerPageOptions, BCS_LEVEL, sortBcsTerms } from '../utils/Constants';
import MediaUploadField from './MediaUploadField';
import MathText from './ui/MathText';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import PassageQuestionWizard from './PassageQuestionWizard';
import CloseButton from './ui/CloseButton';
import YearPicker from './ui/YearPicker';

// Admin screen for reading-comprehension / listening passages. Filter bar + grid mirror
// Question Management. Flow: create a passage → immediately add its questions one-by-one
// (full question form, with Add-Another / Finish / Discard), navigate/discard them in the
// embedded grid, then finish. The passage lives once; when any member enters a quiz the
// backend pulls the whole group in.

const emptyForm = { level: '', subject: '', exam: '', division: '', unit: '', year: '', bcs_term: '', passage_text: '', audio_url: '', wordBank: [], passageImageUrl: '', metadata: null };
const emptyFilters = { level: '', division: '', unit: '', subject: '', exam: '', year: '', search: '' };

// IELTS "Reading-Summary (Word Bank)": the passage carries a shared word bank (A→word) shown once
// above the typed blanks. Editor rows ↔ the stored {A: "...", ...} dict (re-lettered by position).
const wbRowsFromDict = (d) =>
    d && typeof d === 'object' ? Object.keys(d).sort().map((k) => String(d[k] ?? '')) : [];
const wbDictFromRows = (rows) => {
    const out = {};
    let n = 0;
    (rows || []).forEach((text) => {
        const t = (text || '').trim();
        if (t) { out[String.fromCharCode(65 + n)] = t; n += 1; }
    });
    return out;
};
const showsWordBank = (form) => {
    const subj = (form.subject || '').trim().toLowerCase();
    return form.level === 'IELTS' && (subj === 'reading' || subj === 'listening');
};
// IELTS Listening passages can carry a shared map/plan/diagram image (shown for the
// "Listening-Plan/Map/Diagram" topic questions).
const showsPassageImage = (form) =>
    form.level === 'IELTS' && (form.subject || '').trim().toLowerCase() === 'listening';

// Default subject by level: Reading for TOEFL/GRE, English otherwise (auto-selected, editable).
function defaultSubjectForLevel(level) {
    return (level === 'TOEFL' || level === 'GRE') ? 'Reading' : 'English';
}


// TAGLEVELS entries are { label, value } and some carry a `submenu` (VARSITY, ENGINEERING).
function renderLevelOptions() {
    return TAGLEVELS.map((item) =>
        item.submenu ? (
            <optgroup key={item.value} label={item.label}>
                {item.submenu.map((sub) => (
                    <option key={sub.value} value={sub.value}>{sub.label}</option>
                ))}
            </optgroup>
        ) : (
            <option key={item.value} value={item.value}>{item.label}</option>
        )
    );
}

export default function PassageGroupManagement() {
    const CallApi = useApiCaller();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    // Tag taxonomy → level-scoped Subject filter dropdown.
    const [tags, setTags] = useState([]);
    // Pagination (server-side; the groups API takes page/limit and returns total).
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [total, setTotal] = useState(0);

    // Pending (in-form) vs applied filters, mirroring Question Management's Apply/Clear.
    const [pending, setPending] = useState(emptyFilters);
    const [applied, setApplied] = useState(emptyFilters);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null); // group_id when editing, null when adding
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [managing, setManaging] = useState(null); // group being managed (questions view)
    const [managingAutoAdd, setManagingAutoAdd] = useState(false);

    // Subjects for the applied level; all subjects when no level is chosen.
    const subjectOptions = [...new Set(
        tags.filter(t => t.tag_type === 'SUBJECT' && (!pending.level || t.level === pending.level)).map(t => t.name)
    )];
    // BCS term options for the passage form (BCS-only), newest-first.
    const bcsTermOptions = sortBcsTerms(
        tags.filter(t => t.tag_type === 'BCS_TERM' && t.level === BCS_LEVEL).map(t => t.name)
    );

    useEffect(() => {
        (async () => {
            const res = await CallApi({ url: getTagUrl(), method: 'GET', token: true });
            setTags(res.success ? (res.data.tags || []) : []);
        })();
    }, [CallApi]);

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        const res = await CallApi({
            url: getAdminQuestionGroupsUrl({
                page,
                limit: pageSize,
                level: applied.level || null,
                division: applied.division || null,
                unit: applied.unit || null,
                subject: applied.subject || null,
                exam: applied.exam || null,
                year: applied.year || null,
                search: applied.search || null,
            }),
            method: 'GET', token: true, payload: null,
        });
        if (res.success) {
            setGroups(res.data.groups || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [CallApi, applied, page, pageSize]);

    useEffect(() => { fetchGroups(); }, [fetchGroups]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // Filter: selecting a level auto-sets exam := level ("exam is the same as level"), the
    // default subject, and — since exam is now set — auto-selects the coupled division/unit.
    const onFilterLevelChange = (level) => {
        setPending(p => ({
            ...p,
            level,
            exam: level,
            subject: '',
            division: isDivisionLevel(level) ? DIVISION_VALUES[0] : '',
            unit: isUnitLevel(level) ? UNIT_VALUES[0] : '',
        }));
    };

    // Form: same behavior — level sets exam, default subject, and auto-selects division/unit.
    const onFormLevelChange = (level) => {
        setForm(f => ({
            ...f,
            level,
            exam: level,
            subject: defaultSubjectForLevel(level),
            division: isDivisionLevel(level) ? DIVISION_VALUES[0] : '',
            unit: isUnitLevel(level) ? UNIT_VALUES[0] : '',
        }));
    };

    const openAdd = () => { setEditingId(null); setForm({ ...emptyForm }); setFormError(''); setShowForm(true); };

    const openEdit = async (groupId) => {
        const res = await CallApi({ url: getAdminQuestionGroupByIdUrl(groupId), method: 'GET', token: true, payload: null });
        if (res.success) {
            const g = res.data;
            setEditingId(g.group_id);
            setForm({ level: g.level || '', subject: g.subject || '', exam: g.exam || g.level || '', division: g.division || '', unit: g.unit || '', year: g.year || '', bcs_term: g.bcs_term || '', passage_text: g.passage_text || '', audio_url: g.audio_url || '', wordBank: wbRowsFromDict((g.metadata || {}).word_bank), passageImageUrl: (g.metadata || {}).passage_image_url || '', metadata: g.metadata || null });
            setFormError('');
            setShowForm(true);
        }
    };

    // "Add Questions to Passage" — save the passage, then jump into the add-questions flow.
    const saveAndAddQuestions = async () => {
        if (!form.level) { setFormError('Level is required.'); return; }
        if (!form.passage_text.trim()) { setFormError('Passage text is required.'); return; }
        if (isDivisionLevel(form.level) && !form.division) { setFormError('Division is required for SSC/HSC passages.'); return; }
        if (isUnitLevel(form.level) && !form.unit) { setFormError('Unit is required for VARSITY passages.'); return; }
        setFormError('');
        setSaving(true);
        // Preserve any existing group metadata; set/clear the shared word bank (IELTS Reading only).
        const wordBankDict = showsWordBank(form) ? wbDictFromRows(form.wordBank) : {};
        const nextMeta = { ...(form.metadata || {}) };
        if (Object.keys(wordBankDict).length > 0) nextMeta.word_bank = wordBankDict;
        else delete nextMeta.word_bank;
        if (showsPassageImage(form) && (form.passageImageUrl || '').trim()) nextMeta.passage_image_url = form.passageImageUrl.trim();
        else delete nextMeta.passage_image_url;
        const payload = constructQuestionGroupPayload({
            passageText: form.passage_text, audioUrl: form.audio_url,
            level: form.level, subject: form.subject, exam: form.exam || form.level, year: form.year,
            division: isDivisionLevel(form.level) ? form.division : '',
            unit: isUnitLevel(form.level) ? form.unit : '',
            bcsTerm: form.level === BCS_LEVEL ? form.bcs_term : '',
            metadata: Object.keys(nextMeta).length > 0 ? nextMeta : null,
        });
        const res = editingId
            ? await CallApi({ url: getAdminQuestionGroupPutUrl(editingId), method: 'PUT', token: true, payload })
            : await CallApi({ url: getAdminQuestionGroupPostUrl(), method: 'POST', token: true, payload });
        setSaving(false);
        if (res.success) {
            setShowForm(false);
            const group = {
                group_id: res.data.group_id,
                level: form.level, subject: form.subject, exam: form.exam || form.level,
                division: form.division, unit: form.unit, year: form.year,
                passage_text: form.passage_text,
            };
            setManaging(group);
            setManagingAutoAdd(!editingId); // pop the add-question modal for a brand-new passage
        }
    };

    const doDelete = async () => {
        const g = confirmDelete;
        setConfirmDelete(null);
        const res = await CallApi({ url: getAdminQuestionGroupDeleteUrl(g.group_id), method: 'DELETE', token: true, payload: null });
        if (res.success) fetchGroups();
    };

    // Shared passage add/edit modal + delete dialog — rendered in both the list view and the
    // add-questions view so the admin can move back and forth (edit passage text ↔ questions).
    const passageModalEl = showForm ? (
        <div className="modal d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document" style={{ maxWidth: '1200px', width: '95vw' }}>
                <div className="modal-content kp-qform">
                    <div className="modal-header">
                        <h5 className="modal-title">{editingId ? `Edit passage #${editingId}` : 'Add passage'}</h5>
                        <CloseButton onClick={() => setShowForm(false)} />
                    </div>
                    <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                        {formError && <div className="alert alert-danger py-2">{formError}</div>}
                        <div className="row g-2">
                            <div className="col-md-6">
                                <label className="form-label mb-0">Level <span className="text-danger">*</span></label>
                                <select className="form-select" value={form.level} onChange={e => onFormLevelChange(e.target.value)}>
                                    <option value="">Select level…</option>
                                    {renderLevelOptions()}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label mb-0">Exam</label>
                                <input className="form-control" value={form.exam} readOnly placeholder="(same as level)" />
                            </div>
                            {(isDivisionLevel(form.level) || isUnitLevel(form.level)) && (
                                <div className="col-md-6">
                                    <label className="form-label mb-0">{isDivisionLevel(form.level) ? 'Division' : 'Unit'} <span className="text-danger">*</span></label>
                                    {isDivisionLevel(form.level) ? (
                                        <select className="form-select" value={form.division} onChange={e => setForm({ ...form, division: e.target.value })}>
                                            <option value="">Select…</option>
                                            {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                    ) : (
                                        <select className="form-select" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                                            <option value="">Select…</option>
                                            {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    )}
                                </div>
                            )}
                            <div className="col-md-6">
                                <label className="form-label mb-0">Subject</label>
                                <input className="form-control" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label mb-0">Exam Year</label>
                                <YearPicker
                                    value={form.year}
                                    onChange={y => setForm({ ...form, year: y })}
                                    placeholder="Select year…"
                                />
                            </div>
                            {form.level === BCS_LEVEL && (
                                <div className="col-md-6">
                                    <label className="form-label mb-0">BCS Term</label>
                                    <select className="form-select" value={form.bcs_term} onChange={e => setForm({ ...form, bcs_term: e.target.value })}>
                                        <option value="">Select…</option>
                                        {bcsTermOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                        {form.bcs_term && !bcsTermOptions.includes(form.bcs_term) && <option value={form.bcs_term}>{form.bcs_term} (custom)</option>}
                                    </select>
                                    <div className="small text-muted mt-1">Sets which BCS previous-year model test this passage's questions join.</div>
                                </div>
                            )}
                            <div className="col-12">
                                <label className="form-label mb-0">Passage text (LaTeX $…$ supported) <span className="text-danger">*</span></label>
                                <textarea className="form-control" rows={6} value={form.passage_text} onChange={e => setForm({ ...form, passage_text: e.target.value })} />
                                {form.passage_text && form.passage_text.indexOf('$') !== -1 && (
                                    <div className="small text-muted mt-1">Preview: <MathText text={form.passage_text} /></div>
                                )}
                            </div>
                            {isLanguageLevel(form.level) && (
                                <div className="col-12">
                                    <MediaUploadField label="Passage audio (optional)" value={form.audio_url} onChange={(url) => setForm({ ...form, audio_url: url })} kind="audio" />
                                </div>
                            )}
                            {showsPassageImage(form) && (
                                <div className="col-12">
                                    <MediaUploadField
                                        label="Passage image — map/plan/diagram (optional; shown for Plan/Map/Diagram questions)"
                                        value={form.passageImageUrl}
                                        onChange={(url) => setForm({ ...form, passageImageUrl: url })}
                                        kind="image"
                                    />
                                </div>
                            )}
                            {showsWordBank(form) && (
                                <div className="col-12">
                                    <div className="border rounded p-2">
                                        <label className="form-label mb-0 fw-semibold">Word bank (A–J)</label>
                                        <div className="small text-muted mb-2">
                                            The shared list of words shown once, read-only, above the summary blanks.
                                            Test-takers type the matching letter (A, B, C…) into each blank.
                                        </div>
                                        {(form.wordBank || []).map((text, i) => (
                                            <div key={i} className="d-flex align-items-center gap-2 mb-1">
                                                <span className="fw-bold" style={{ width: '1.5rem' }}>{String.fromCharCode(65 + i)}.</span>
                                                <input
                                                    className="form-control form-control-sm"
                                                    value={text}
                                                    placeholder="word / phrase"
                                                    onChange={(e) => setForm({ ...form, wordBank: form.wordBank.map((t, j) => (j === i ? e.target.value : t)) })}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setForm({ ...form, wordBank: form.wordBank.filter((_, j) => j !== i) })}
                                                    title="Remove"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                        {(form.wordBank || []).length < 10 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary mt-1"
                                                onClick={() => setForm({ ...form, wordBank: [...(form.wordBank || []), ''] })}
                                            >
                                                + Add option
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={saveAndAddQuestions} disabled={!form.level || !form.passage_text.trim() || (isDivisionLevel(form.level) && !form.division) || (isUnitLevel(form.level) && !form.unit) || saving}>
                            {managing ? 'Save passage & continue' : 'Add Questions to Passage'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    ) : null;

    const deleteModalEl = confirmDelete ? (
        <ConfirmDeleteDialog
            open={true}
            title="Delete passage?"
            message={`Delete Passage #${confirmDelete.group_id}? Its questions are detached (kept as standalone), not deleted. Blocked if any member is already used in a quiz.`}
            onConfirm={doDelete}
            onCancel={() => setConfirmDelete(null)}
        />
    ) : null;

    // --- Add-questions flow: a linear wizard over the passage's questions (Back/Next), or the
    // passage-text modal when editing it (rendered instead of the wizard to avoid stacking). ---
    if (managing) {
        return (
            <div className="container-fluid p-3">
                {showForm ? passageModalEl : (
                    <PassageQuestionWizard
                        key={managing.group_id}
                        group={managing}
                        onExit={() => { setManaging(null); setManagingAutoAdd(false); fetchGroups(); }}
                        onEditPassage={() => openEdit(managing.group_id)}
                    />
                )}
                {deleteModalEl}
            </div>
        );
    }

    // --- List view: filter bar (Question-Filters style) + passages grid ---
    return (
        <div className="container-fluid p-4">
            <h1 className="fs-2 fw-bold text-dark mb-3">Passage Management</h1>

            <div className="card shadow-sm mb-4 kp-qfilter">
                <div className="card-body">
                    <h5 className="card-title text-dark mb-3">Passage Filters</h5>
                    <div className="row g-3 align-items-end">
                        <div className="col-6 col-md-3">
                            <label className="form-label text-gray-700">Level</label>
                            <select className="form-select" value={pending.level} onChange={(e) => onFilterLevelChange(e.target.value)}>
                                <option value="">All Levels</option>
                                {renderLevelOptions()}
                            </select>
                        </div>
                        {isDivisionLevel(pending.level) && (
                            <div className="col-6 col-md-3">
                                <label className="form-label text-gray-700">Division</label>
                                <select className="form-select" value={pending.division} onChange={(e) => setPending({ ...pending, division: e.target.value })}>
                                    <option value="">All Divisions</option>
                                    {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                        )}
                        {isUnitLevel(pending.level) && (
                            <div className="col-6 col-md-3">
                                <label className="form-label text-gray-700">Unit</label>
                                <select className="form-select" value={pending.unit} onChange={(e) => setPending({ ...pending, unit: e.target.value })}>
                                    <option value="">All Units</option>
                                    {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="col-6 col-md-3">
                            <label className="form-label text-gray-700">Subject</label>
                            <select className="form-select" value={pending.subject} onChange={(e) => setPending({ ...pending, subject: e.target.value })}>
                                <option value="">All Subjects</option>
                                {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="col-6 col-md-3">
                            <label className="form-label text-gray-700">Exam</label>
                            <input className="form-control" value={pending.exam} readOnly placeholder="(same as level)" />
                        </div>
                    </div>
                    <div className="row g-3 mt-1 align-items-end">
                        <div className="col-6 col-md-3">
                            <label className="form-label text-gray-700">Exam Year</label>
                            <YearPicker
                                value={pending.year}
                                onChange={y => setPending({ ...pending, year: y })}
                                placeholder="All Exam Years"
                            />
                        </div>
                        <div className="col-6 col-md-4">
                            <label className="form-label text-gray-700">Search By Text</label>
                            <input className="form-control" value={pending.search} onChange={(e) => setPending({ ...pending, search: e.target.value })} placeholder="Search in passages..." />
                        </div>
                        <div className="col-12 col-md-5 d-flex gap-2">
                            <button className="btn btn-primary" onClick={() => { setPage(1); setApplied(pending); }}>Apply Filters</button>
                            <button className="btn btn-outline-secondary" onClick={() => { setPage(1); setPending(emptyFilters); setApplied(emptyFilters); }}>Clear Filters</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted small">Total: {total} passage{total === 1 ? '' : 's'}</span>
                <button className="btn btn-success" onClick={openAdd}><Plus size={16} /> Add Passage</button>
            </div>

            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Passage</th>
                            <th>Level</th>
                            <th>Division / Unit</th>
                            <th>Subject</th>
                            <th>Exam</th>
                            <th>Year</th>
                            <th className="text-center">Questions</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center text-muted py-3">Loading…</td></tr>
                        ) : groups.length === 0 ? (
                            <tr><td colSpan={8} className="text-center text-muted py-3">No passages match the filter.</td></tr>
                        ) : groups.map(g => (
                            <tr key={g.group_id}>
                                <td style={{ maxWidth: 380 }}>
                                    <div className="fw-semibold">Passage #{g.group_id}</div>
                                    <div className="small text-muted text-truncate"><MathText text={(g.passage_text || '').slice(0, 140)} /></div>
                                </td>
                                <td>{g.level}</td>
                                <td>{(isUnitLevel(g.level) ? g.unit : isDivisionLevel(g.level) ? g.division : (g.division || g.unit)) || '—'}</td>
                                <td>{g.subject || '—'}</td>
                                <td>{g.exam || '—'}</td>
                                <td>{g.year || '—'}</td>
                                <td className="text-center"><span className="badge bg-primary-subtle text-primary">{g.question_count}</span></td>
                                <td className="text-end">
                                    <div className="btn-group btn-group-sm">
                                        <button className="btn btn-outline-primary" onClick={() => { setManaging(g); setManagingAutoAdd(false); }} title="Manage questions"><ListChecks size={14} /> Questions</button>
                                        <button className="btn btn-outline-secondary" onClick={() => openEdit(g.group_id)} title="Edit passage"><Edit size={14} /></button>
                                        <button className="btn btn-outline-danger" onClick={() => setConfirmDelete(g)} title="Delete passage"><Trash2 size={14} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination + items-per-page */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                <div className="d-flex align-items-center gap-2">
                    <label htmlFor="passagePageSize" className="form-label text-secondary mb-0">Items per page:</label>
                    <select
                        id="passagePageSize"
                        className="form-select form-select-sm w-auto"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    >
                        {itemsPerPageOptions.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
                    <span className="text-secondary">Page {page} of {totalPages} · Total: {total}</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
                </div>
            </div>

            {passageModalEl}
            {deleteModalEl}
        </div>
    );
}
